import { lazy, Suspense, useEffect, useMemo } from 'react'
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router'
import { ApiError, api, setUnauthorizedHandler, shouldRetry } from '@/api/client'
import { setCsrfToken } from '@/api/session'
import { Button } from '@/kit'
import { loadExtension } from '@/extensions/loader'
import { ERP_BASE, useErpEntries } from '@/modules/erp/nav'
import { inMainMenu } from '@/extensions/types'
import type { ExtensionView, Locale, LocalizedText } from '@/extensions/types'
import { PermissionsContext } from '@/sdk/permissions'
import { TenantContext } from '@/sdk/tenant'
import { useActivityTracking } from './activity'
import { APP_BASE } from './base'
import { ExtensionBoundary } from './ExtensionBoundary'
import { showFavicon } from './favicon'
import i18n from './i18n'
import { LoginPage } from './LoginPage'
import { safeReturnTo } from './returnTo'
import { AppFooter } from './shell/AppFooter'
import { FloatingLinks } from './shell/FloatingLinks'
import { TopNav } from './shell/TopNav'
import type { NavEntry } from './shell/TopNav'
import { platformVersion, UpdateNotice } from './shell/UpdateNotice'
import { fetchExtensionManifest, fetchSession, fetchTenantSettings, tenantSlugFromHost } from './tenant'
import type { SessionInfo, TenantSettings } from './tenant'
import { tenantMenu } from './tenantMenu'
import { SetPasswordPage } from './password/SetPasswordPage'
import { SET_PASSWORD_PATH } from './password/passwordApi'
import { TeamPage } from './team/TeamPage'
import type { TeamPageRef } from './team/TeamPage'
import { TEAM_PATH } from './team/teamApi'
import { USAGE_PATH } from './usage/usageApi'
import { isAdmin, splitViews } from './viewAccess'

// The ERP (Odoo) for tenants that have it: its screens load only when one is opened.
const ErpArea = lazy(() => import('@/modules/erp/ErpArea'))

const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: shouldRetry, refetchOnWindowFocus: false } },
})

function localized(text: LocalizedText, locale: string, fallback: string): string {
    return text[locale as Locale] ?? text.en ?? fallback
}

function viewTitle(view: ExtensionView, locale: string): string {
    return localized(view.title, locale, view.id)
}

/**
 * The main menu: one item per view, except views of a section the extension names
 * (`sections`), which share one dropdown at the place of the first of them.
 */
export function menuEntries(views: ExtensionView[], sections: Record<string, LocalizedText> | undefined, locale: string): NavEntry[] {
    const entries: NavEntry[] = []
    const groups = new Map<string, NavEntry>()
    for (const view of views) {
        const entry: NavEntry = { key: view.id, label: viewTitle(view, locale), to: view.path }
        const title = view.section ? sections?.[view.section] : undefined
        if (!view.section || !title) {
            entries.push(entry)
            continue
        }
        let group = groups.get(view.section)
        if (!group) {
            group = { key: `section-${view.section}`, label: localized(title, locale, view.section), children: [] }
            groups.set(view.section, group)
            entries.push(group)
        }
        group.children!.push(entry)
    }
    return entries
}

/** An old address: the same page at its new one, the query string kept. */
function Redirect({ to }: { to: string }) {
    const location = useLocation()
    return <Navigate to={`${to}${location.search}`} replace />
}

/**
 * The usage page's old address (/usage, until 2026-10-06): now a tab of Users & roles. Its period,
 * client, staff switch and open user are kept; its users/views switch is `by` there.
 */
function UsageRedirect() {
    const location = useLocation()
    const old = new URLSearchParams(location.search)
    const next = new URLSearchParams({ tab: 'usage' })
    for (const [key, value] of old) {
        if (key === 'tab') {
            if (value === 'views') next.set('by', 'views')
        } else next.append(key, value)
    }
    return <Navigate to={`${TEAM_PATH}?${next}`} replace />
}

/** Signed in on /login: on to the page asked for (`returnTo`, checked), not just "/". */
function AfterSignIn() {
    const location = useLocation()
    return <Navigate to={safeReturnTo(new URLSearchParams(location.search).get('returnTo'))} replace />
}

/** The earlier app's sign-in link (`/sign-in?redirectUrl=…`): this app's, with `returnTo`. */
function OldSignIn() {
    const location = useLocation()
    const back = new URLSearchParams(location.search).get('redirectUrl')
    return <Navigate to={back ? `/login?returnTo=${encodeURIComponent(back)}` : '/login'} replace />
}

function Message({ testId, children }: { testId: string; children: string }) {
    return (
        <p data-testid={testId} role="status" className="m-0 px-[var(--layout-page-padding-x)] py-[32px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">
            {children}
        </p>
    )
}

const LANGUAGES = ['de', 'et', 'en', 'ru']
const LANGUAGE_LABELS: Record<string, string> = { en: 'English', de: 'Deutsch', et: 'Eesti', ru: 'Русский' }

/** The viewer's language choice, per tenant and browser (a convenience: may be unavailable). */
function storedLanguage(slug: string): string | null {
    try {
        return window.localStorage.getItem(`distribuai.language.${slug}`)
    } catch {
        return null
    }
}
function storeLanguage(slug: string, code: string) {
    try {
        window.localStorage.setItem(`distribuai.language.${slug}`, code)
    } catch {
        // Private mode or blocked storage: the choice just isn't remembered.
    }
}

/** Sales demos: tenants flagged `demo`, and every tenant on a demo host. */
function isDemo(settings: TenantSettings['data'] | undefined): boolean {
    return settings?.theme?.demo === true || /\.demo\.(distribuai\.eu|localhost)$/.test(window.location.hostname)
}

/** Signed-in layout: top bar from the tenant's views, the page, the footer. */
function AppLayout({
    tenant,
    logoHeight,
    demoLabel,
    languages,
    language,
    onLanguage,
    session,
    views,
    sections,
    extensionProblem,
    extensionVersion,
    erp,
}: {
    tenant: TenantSettings['data']['tenant']
    logoHeight?: number | null
    demoLabel: string | null
    languages: string[]
    language: string | null
    onLanguage: (code: string) => void
    session: SessionInfo['data']
    views: ExtensionView[]
    sections?: Record<string, LocalizedText>
    extensionProblem: boolean
    extensionVersion: string | null
    /** The tenant has the ERP: its apps join the top bar, its screens are under /erp. */
    erp: boolean
}) {
    const { t, i18n: i18nInstance } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const client = useQueryClient()
    const current = views.find((view) => location.pathname === view.path || location.pathname.startsWith(`${view.path}/`))
    const tenantInfo = useMemo(() => ({ slug: tenant.slug, name: tenant.name, demo: demoLabel !== null }), [tenant.slug, tenant.name, demoLabel])

    // Users & roles (with its usage tab) is the platform's own page, not an extension view.
    const at = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`)
    const platformPage = at(TEAM_PATH) ? { key: 'team', title: t('team.title') } : erp && at(ERP_BASE) ? { key: 'erp', title: t('erp.menu') } : null
    const erpEntries = useErpEntries(erp, location.pathname, { erp: t('erp.menu'), settings: t('erp.settings') })
    const currentTitle = current ? viewTitle(current, i18nInstance.language) : (platformPage?.title ?? '')
    useActivityTracking({
        enabled: true,
        pathname: location.pathname,
        search: location.search,
        routeKey: current?.id ?? platformPage?.key ?? 'unknown',
        title: currentTitle,
    })
    // The browser tab: the page, then the company ("Laoseisud · Tradehouse"). The ERP's screens
    // name themselves (modules/erp/title.ts).
    const inErp = platformPage?.key === 'erp'
    useEffect(() => {
        if (inErp) return
        document.title = currentTitle ? `${currentTitle} · ${tenant.name}` : tenant.name
    }, [currentTitle, tenant.name, inErp])

    // Which build this tab runs (support asks; a stale tab is the usual "I don't see it").
    const platform = platformVersion()
    const versionLabel = platform ? [t('app.version', { version: platform }), extensionVersion !== platform ? extensionVersion : null].filter(Boolean).join(' · ') : null

    async function signOut() {
        try {
            await api.post('/api/auth/logout.php', {}, { handleUnauthorized: false })
        } catch {
            // The session may already be gone; sign out locally either way.
        }
        setCsrfToken(null)
        client.clear()
        navigate('/login', { replace: true })
    }

    return (
        <div className="flex min-h-screen flex-col bg-[var(--surface-default)]">
            <TopNav
                logoUrl={tenant.logoUrl ?? undefined}
                tenantName={tenant.name}
                logoHeight={logoHeight}
                demoLabel={demoLabel}
                demoHint={demoLabel ? t('app.demoHint') : null}
                languages={languages.map((code) => ({ code, label: LANGUAGE_LABELS[code] ?? code }))}
                language={language ?? undefined}
                languageLabel={t('app.language')}
                onLanguage={onLanguage}
                userName={session.full_name || session.email}
                menuLabel={t('app.userMenu')}
                navMenuLabel={t('app.navMenu')}
                signOutLabel={t('app.signOut')}
                onSignOut={signOut}
                versionLabel={versionLabel}
                entries={[...menuEntries(views.filter(inMainMenu), sections, i18nInstance.language), ...erpEntries]}
                menuLinks={[
                    // Users & roles (its tabs: users, roles, usage): superadmins and tenant admins only
                    // (the API refuses everyone else too).
                    ...(isAdmin(session) ? [{ key: 'team', label: t('team.menu'), to: TEAM_PATH }] : []),
                    ...views.filter((view) => view.placement === 'user-menu').map((view) => ({ key: view.id, label: viewTitle(view, i18nInstance.language), to: view.path })),
                ]}
            />
            <main className="flex w-full min-w-0 flex-1 flex-col">
                {extensionProblem && <Message testId="extension-not-loaded">{t('extension.notLoaded')}</Message>}
                <TenantContext.Provider value={tenantInfo}>
                    <PermissionsContext.Provider value={session.permissions ?? null}>
                        <Outlet />
                    </PermissionsContext.Provider>
                </TenantContext.Provider>
            </main>
            <AppFooter />
            <FloatingLinks links={views.filter((view) => view.placement === 'floating').map((view) => ({ key: view.id, label: viewTitle(view, i18nInstance.language), to: view.path }))} />
            <UpdateNotice slug={tenant.slug} extensionVersion={extensionVersion} />
        </div>
    )
}

function Shell() {
    const { t, i18n: i18nInstance } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const client = useQueryClient()
    const slug = useMemo(() => tenantSlugFromHost(), [])

    // Any 401 from the API: the session is gone. Back to login, keeping where we were.
    useEffect(() => {
        setUnauthorizedHandler(() => {
            setCsrfToken(null)
            client.removeQueries({ queryKey: ['session'] })
            const here = `${window.location.pathname.slice(APP_BASE.length - 1)}${window.location.search}`
            navigate(`/login?returnTo=${encodeURIComponent(here)}`, { replace: true })
        })
        return () => setUnauthorizedHandler(null)
    }, [client, navigate])

    // Tenant settings and the session load in parallel.
    const settings = useQuery({
        queryKey: ['tenant-settings', slug],
        queryFn: ({ signal }) => fetchTenantSettings(slug!, signal),
        enabled: slug !== null,
        staleTime: Infinity,
    })
    // Languages the tenant offers (its default first) that the app has strings for. The
    // viewer's remembered choice wins; otherwise the tenant's default.
    const theme = settings.data?.data.theme
    const languages = useMemo(
        () => [...new Set([theme?.defaultLanguage, ...(theme?.availableLanguages ?? [])].filter((code): code is string => !!code && LANGUAGES.includes(code)))],
        [theme?.defaultLanguage, theme?.availableLanguages],
    )
    // The current language lives in i18next (useTranslation re-renders on a change).
    const applyLanguage = (code: string) => {
        void i18nInstance.changeLanguage(code)
        document.documentElement.lang = code
    }
    useEffect(() => {
        if (!slug || languages.length === 0) return
        const remembered = storedLanguage(slug)
        const code = remembered && languages.includes(remembered) ? remembered : languages[0]!
        void i18nInstance.changeLanguage(code)
        document.documentElement.lang = code
    }, [slug, languages, i18nInstance])
    const chooseLanguage = (code: string) => {
        applyLanguage(code)
        if (slug) storeLanguage(slug, code)
    }

    const session = useQuery({
        queryKey: ['session'],
        queryFn: ({ signal }) => fetchSession(signal),
    })
    const signedIn = session.isSuccess
    useEffect(() => {
        if (session.data) setCsrfToken(session.data.data.csrf_token)
    }, [session.data])
    // Signed in on the sign-in page: the settings loaded then are the sign-in page's ("login"
    // scope, branding only). Fetch them again for the app (the tenant's menu, among others),
    // and show the app once they are there, so a tenant's menu never flashes the full one.
    const settingsForSignIn = signedIn && settings.data?.scope === 'login'
    useEffect(() => {
        if (settingsForSignIn) void client.invalidateQueries({ queryKey: ['tenant-settings', slug] })
    }, [settingsForSignIn, client, slug])

    // The app's parts the tenant has (analytics without a setting): the analytics are its
    // extension's views; the ERP is Odoo's, under /erp.
    const modules = theme?.modules ?? ['analytics']
    const analytics = modules.includes('analytics')
    const erp = modules.includes('erp')

    // A tenant's bespoke code is only fetched for a signed-in user of that tenant.
    const manifest = useQuery({
        queryKey: ['extension-manifest', slug],
        queryFn: ({ signal }) => fetchExtensionManifest(slug!, signal),
        enabled: signedIn && slug !== null && analytics,
        staleTime: Infinity,
    })
    const extension = useQuery({
        queryKey: ['extension', manifest.data?.url],
        queryFn: () => loadExtension(manifest.data!),
        enabled: Boolean(manifest.data),
        staleTime: Infinity,
        retry: false,
    })
    const definition = extension.data?.status === 'loaded' ? extension.data.definition : undefined
    // Without analytics (an ERP-only tenant) no view of the extension shows, even one loaded at sign-in
    // (the sign-in page's settings don't say which parts the tenant has).
    const allViews = settingsForSignIn || !analytics ? [] : (definition?.views ?? [])
    // Views for sales demos only (e.g. the product tour) stay out of real tenants' apps.
    // A demo host is a demo before its settings arrive, so its tab icon is orange from the start.
    const demoTenant = isDemo(settings.data?.data)
    useEffect(() => showFavicon(demoTenant), [demoTenant])
    const tenantViews = allViews.filter((view) => view.audience !== 'demo' || demoTenant)
    // The pages the user's role may open (viewAccess.ts); the others leave the menu and the routes.
    const sessionData = session.data?.data
    const access = splitViews(tenantViews, sessionData?.permissions)
    // The tenant's own menu, when it has one (tenantMenu.ts).
    const menu = tenantMenu(access.allowed, theme?.menu)
    const views = menu.views
    // The Users & roles grid: every page of this tenant's app, in the reader's language.
    const teamPages: TeamPageRef[] = tenantViews.map((view) => ({
        id: view.id,
        title: viewTitle(view, i18nInstance.language),
        group: view.section && definition?.sections?.[view.section] ? localized(definition.sections[view.section]!, i18nInstance.language, view.section) : null,
    }))
    const routed = (path: string) => path === '/' || views.some((view) => path === view.path || path.startsWith(`${view.path}/`))
    // The usage tab names a visited path by its view's title in the reader's language.
    const usageViewTitle = (path: string) => {
        const bare = path.split('?')[0] ?? path
        const view = allViews.find((v) => bare === v.path || bare.startsWith(`${v.path}/`))
        return view ? viewTitle(view, i18nInstance.language) : null
    }
    // Old addresses that moved, never one a view now owns. The views this tenant doesn't show
    // open the landing page, at their own address and at their old ones.
    const redirects: [string, string][] = [
        ...Object.entries(definition?.redirects ?? {})
            .filter(([from]) => !allViews.some((view) => view.path === from))
            .map(([from, to]): [string, string] => [from, routed(to) ? to : '/']),
        ...menu.hidden.map((view): [string, string] => [`${view.path}/*`, '/']),
        ...access.denied.map((view): [string, string] => [`${view.path}/*`, '/']),
    ]
    // "/" goes to the first view of the main navigation.
    const landing = views.find(inMainMenu)
    const extensionProblem = manifest.isError || (extension.data !== undefined && extension.data.status !== 'loaded')
    const extensionSettled = !settingsForSignIn && (!analytics || manifest.isError || manifest.data === null || extension.data !== undefined)

    if (!slug || settings.isError) return <Message testId="tenant-unknown">{t('app.tenantUnknown')}</Message>
    if (settings.isPending || session.isPending) return <Message testId="app-loading">{t('app.loading')}</Message>
    // Only a 401 means signed out. A server that can't be reached right now (a restart, a
    // deploy) is not a sign-out: say so and offer a reload, never the sign-in page.
    if (session.isError && !(session.error instanceof ApiError && session.error.status === 401)) {
        return (
            <div className="flex flex-col items-start gap-[12px] px-[var(--layout-page-padding-x)] py-[32px]">
                <p data-testid="session-unreachable" role="status" className="m-0 text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">
                    {t('app.unreachable')}
                </p>
                <Button variant="secondary" onClick={() => void session.refetch()}>
                    {t('app.retry')}
                </Button>
            </div>
        )
    }
    const tenant = settings.data.data.tenant
    const logoHeight = settings.data.data.theme?.logoHeight
    const demoLabel = isDemo(settings.data.data) ? t('app.demo') : null

    return (
        <Routes>
            <Route path="/sign-in" element={<OldSignIn />} />
            <Route
                path="/login"
                element={
                    signedIn ? (
                        <AfterSignIn />
                    ) : (
                        <LoginPage
                            tenant={tenant}
                            logoHeight={logoHeight}
                            demoLabel={demoLabel}
                            demoHint={demoLabel ? t('app.demoHint') : null}
                            ssoProviders={settings.data.data.theme?.ssoProviders}
                        />
                    )
                }
            />
            {/* A set-password link's page: public, the link's token is what authenticates it. */}
            <Route path={SET_PASSWORD_PATH} element={<SetPasswordPage tenant={tenant} logoHeight={logoHeight} demoLabel={demoLabel} demoHint={demoLabel ? t('app.demoHint') : null} />} />
            <Route
                element={
                    signedIn ? (
                        <AppLayout
                            tenant={tenant}
                            logoHeight={logoHeight}
                            demoLabel={demoLabel}
                            languages={languages}
                            language={i18nInstance.language}
                            onLanguage={chooseLanguage}
                            session={session.data.data}
                            views={views}
                            sections={menu.flat ? undefined : definition?.sections}
                            extensionProblem={extensionProblem}
                            extensionVersion={manifest.data?.version ?? null}
                            erp={erp && !settingsForSignIn}
                        />
                    ) : (
                        <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />
                    )
                }
            >
                <Route
                    index
                    element={
                        landing ? (
                            <Navigate to={landing.path} replace />
                        ) : erp && !analytics ? (
                            // ERP only: its overview.
                            <Navigate to={ERP_BASE} replace />
                        ) : !extensionSettled ? (
                            <Message testId="app-loading">{t('app.loading')}</Message>
                        ) : tenantViews.some(inMainMenu) ? (
                            // The app has pages, but this role may open none of them.
                            <Message testId="no-pages">{t('access.noPages')}</Message>
                        ) : null
                    }
                />
                <Route path="/home" element={<Navigate to="/" replace />} />
                {redirects.map(([from, to]) => (
                    <Route key={from} path={from} element={<Redirect to={to} />} />
                ))}
                <Route path={USAGE_PATH} element={<UsageRedirect />} />
                {erp && (
                    <Route
                        path={`${ERP_BASE}/*`}
                        element={
                            <Suspense fallback={<Message testId="app-loading">{t('app.loading')}</Message>}>
                                <ErpArea language={i18nInstance.language} tenantName={tenant.name} />
                            </Suspense>
                        }
                    />
                )}
                <Route
                    path={TEAM_PATH}
                    element={sessionData && isAdmin(sessionData) ? <TeamPage pages={teamPages} titleFor={usageViewTitle} /> : <Message testId="not-found">{t('app.notFound')}</Message>}
                />
                {views.map((view) => (
                    <Route
                        key={view.id}
                        path={`${view.path}/*`}
                        element={
                            // Keyed: without it React reuses one boundary across routes,
                            // so a crashed view would also blank the next one.
                            <ExtensionBoundary key={view.id} viewId={view.id}>
                                <view.component />
                            </ExtensionBoundary>
                        }
                    />
                ))}
                <Route path="*" element={extensionSettled ? <Message testId="not-found">{t('app.notFound')}</Message> : <Message testId="app-loading">{t('app.loading')}</Message>} />
            </Route>
        </Routes>
    )
}

export function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>
                <BrowserRouter basename={APP_BASE}>
                    <Shell />
                </BrowserRouter>
            </I18nextProvider>
        </QueryClientProvider>
    )
}
